import _cloneDeep from 'lodash/cloneDeep';
import _isString from 'lodash/isString';
import generator from 'lucene-query-generator';

/**
 * Lucene class with helper methods to operate with lucene compatible API services.
 */
export default class Lucene {
    /**
     * Convert data to lucene compatible string
     * @param {{metadata: Object, state: Object, alerts: Object, users: Array<number>, devices: Object}} data - Object
     * for conversion
     * @returns {string} Return lucene compatible string
     * @static
     * @example
     * Lucene.convertToString({
     *       metadata: {
     *           name: 'Test',
     *           photo_url: 'url',
     *       },
     *       state: {
     *           battery: {
     *               above: 10,
     *               below: 50
     *           },
     *           rssi: {
     *               above: 5,
     *               below: 20
     *           },
     *           starred: true
     *       },
     *       alerts: {
     *           status: [0, 1]
     *       },
     *       users: [0, 1, 2],
     *       devices: [
     *           {
     *               type: 'temperature',
     *               state: {
     *                   above: 5,
     *                   below: 10
     *               },
     *           },
     *           {
     *               type: 'door',
     *               state: {
     *                   open: false,
     *                   closed: true
     *               },
     *           },
     *           {
     *               type: 'window',
     *               state: {
     *                   open: false,
     *                   closed: true
     *               },
     *           },
     *           {
     *               type: 'theft',
     *               state: {
     *                   present: true,
     *                   absent: false
     *               },
     *           },
     *           {
     *               type: 'cloud-connector',
     *               state: {
     *                   withSensors: true,
     *                   withoutSensors: false
     *               },
     *           }
     *       ]
     *   });
     */
    static convertToString(data) {
        const schema = {};
        let operands = [];

        function recursiveLocalOperandsJoinAnd(parsed, parsedItems, items, recursiveOperands) {
            if (parsed && parsedItems < items && recursiveOperands.length === parsedItems) {
                return ` (${recursiveOperands.join(' AND ')}) `;
            }

            return recursiveOperands;
        }

        function recursiveLocalOperandsFlatArrayOrReturn(recursiveOperands) {
            if (Array.isArray(recursiveOperands) && recursiveOperands.length === 1) {
                return recursiveOperands[0].trim();
            }

            return recursiveOperands;
        }

        function recursiveLocalOperandsParseOperandsResult(result, draftOperands, recursiveOperands) {
            let localResult = result;

            if (localResult === false) {
                localResult = ` (${generator.convert(draftOperands, { schema })}) `;
                recursiveOperands.push(localResult);
            } else if (Array.isArray(localResult)) {
                if ('$operator' in draftOperands) {
                    const operator = ` ${draftOperands.$operator.toUpperCase()} `;

                    localResult = ` (${localResult.join(operator)}) `;
                }
                recursiveOperands.push(localResult);
            } else {
                recursiveOperands.push(localResult);
            }
        }

        function recursiveLocalOperandsIsParsed(parsed, recursiveOperands) {
            if (!parsed) {
                return false;
            }

            return recursiveLocalOperandsFlatArrayOrReturn(recursiveOperands);
        }

        function recursiveLocalOperands(draftOperands) {
            let recursiveOperands = [];
            if (Array.isArray(draftOperands)) {
                let parsed = false;
                let parsedItems = 0;
                const items = draftOperands.length;
                for (let i = 0; i < items; ++i) {
                    const draftOperand = draftOperands[i];
                    if ('$operands' in draftOperand) {
                        const result = recursiveLocalOperands(draftOperand);
                        if (Array.isArray(result) && result.length === 1) {
                            recursiveOperands.push(result[0].trim());
                        } else {
                            recursiveOperands.push(result);
                        }
                        parsed = true;
                        ++parsedItems;
                    } else {
                        recursiveOperands.push(generator.convert({ $operands: draftOperand }, { schema }));
                    }
                }

                recursiveOperands = recursiveLocalOperandsJoinAnd(parsed, parsedItems, items, recursiveOperands);

                return recursiveLocalOperandsIsParsed(parsed, recursiveOperands);
            } else if ('$operands' in draftOperands) {
                recursiveLocalOperandsParseOperandsResult(
                    recursiveLocalOperands(draftOperands.$operands),
                    draftOperands,
                    recursiveOperands
                );
            } else {
                return false;
            }

            return recursiveLocalOperandsFlatArrayOrReturn(recursiveOperands);
        }

        const localData = JSON.parse(JSON.stringify(_cloneDeep(data)));
        /* {
            metadata: {
                name: 'Test',
                photo_url: 'url'
            },
            state: {
                battery: {
                    above: 10,
                        below: 50
                },
                rssi: {
                    from: 5,
                        to: 20
                },
                starred: true
            },
             alerts: {
                status: [0, 1]
             },
            users: [0, 1, 2],
            devices: [
                {
                    type: 'temperature',
                     state: {
                         above: 5,
                         below: 10
                     },
                },
                {
                    type: 'door',
                     state: {
                         open: false,
                         closed: true
                     },
                },
                {
                    type: 'window',
                     state: {
                         open: false,
                         closed: true
                     },
                },
                {
                    type: 'theft',
                     state: {
                         present: true,
                         absent: false
                     },
                },
                {
                    type: 'cloud-connector',
                     state: {
                         withSensors: true,
                         withoutSensors: false
                     },
                }
            ]
        } */

        const attributeTypes = {
            'metadata': (config) => {
                const result = Lucene.convertMetadataToLuceneCompatible(config);

                Object.assign(schema, result.schema);
                operands = operands.concat(result.operands);
            },
            'state': (config) => {
                const result = Lucene.convertStateToLuceneCompatible(config);

                Object.assign(schema, result.schema);
                operands = operands.concat(result.operands);
            },
            'alerts': (config) => {
                const result = Lucene.convertAlertsToLuceneCompatible(config);

                Object.assign(schema, result.schema);
                operands = operands.concat(result.operands);
            },
            'users': (config) => {
                const result = Lucene.convertUsersToLuceneCompatible(config);

                Object.assign(schema, result.schema);
                operands = operands.concat(result.operands);
            },
            'folders': (config) => {
                const result = Lucene.convertFoldersToLuceneCompatible(config);

                operands.push(result);
            },
            'labels': (config) => {
                const result = Lucene.convertLabelsToLuceneCompatible(config);

                operands.push(result);
            },
            'geo_distance': (config) => {
                const result = Lucene.convertGeoDistanceToLuceneCompatible(config);

                operands.push(result);
            },
            'devices': (config) => {
                const result = Lucene.convertThingsToLuceneCompatible(config);

                Object.assign(schema, result.schema);
                operands = operands.concat(result.operands);
            },
            registered: (config) => {
                const result = Lucene.convertDateRangeToLuceneCompatible(config);

                operands.push({
                    registered: result
                });
            },
            last_updated: (config) => {
                const result = Lucene.convertDateRangeToLuceneCompatible(config);

                operands.push({
                    last_updated: result
                });
            },
            'default': (config, unspecifiedKey) => {
                const tempObject = {};
                tempObject[unspecifiedKey] = 'Unspecified key';

                operands.push(tempObject);
            }
        };

        Object.keys(localData).forEach(key => (attributeTypes[key] || attributeTypes.default)(localData[key], key));

        if (operands.length) {
            const localOperands = [];

            operands.forEach((operand) => {
                if (_isString(operand)) {
                    localOperands.push(operand);
                } else if ('$operands' in operand) {
                    localOperands.push(recursiveLocalOperands(operand));
                } else {
                    localOperands.push(generator.convert({ $operands: operand }, { schema }));
                }
            });

            return localOperands.join(' AND ');
        }

        return generator.convert({ $operator: 'and', $operands: operands }, { schema });
    }

    /**
     * Convert state object to lucene builder compatible object
     * @param {{battery: Object, rssi: Object, starred: boolean}} data - State config
     * @returns {{schema: {}, operands: Array}} Return lucene builder object
     * @static
     */
    static convertStateToLuceneCompatible(data) {
        /*
        {
            battery: {
                above: 10,
                below: 50
            },
            rssi: {
                above: 5,
                bellow: 20
            },
            starred: true
        }
        */
        const schema = {};
        const operands = [];

        const types = {
            'battery': (config) => {
                const key = 'state.properties.battery_percentage';
                const value = Lucene.convertBatteryToLucene(config);

                if (value) {
                    const tempObject = {};
                    tempObject[key] = value;

                    operands.push(tempObject);
                }
            },
            'rssi': (config) => {
                const key = 'state.properties.rssi_strength';
                const value = Lucene.convertRssiToLucene(config);

                if (value) {
                    const tempObject = {};
                    tempObject[key] = value;

                    operands.push(tempObject);
                }
            },
            'starred': (config) => {
                const key = 'starred';
                const value = config;

                if (value) {
                    const tempObject = {};
                    tempObject[key] = value.toString();

                    operands.push(tempObject);
                }
            },
            'default': (config, unspecifiedKey) => {
                const tempObject = {};
                tempObject[`state.${unspecifiedKey}`] = 'Unspecified key';

                operands.push(tempObject);
            }
        };

        Object.keys(data).forEach(key => (types[key] || types.default)(data[key], key));

        return { schema, operands };
    }

    /**
     * Convert metadata object to lucene builder compatible object
     * @param {{name: string, photo_url: string}} data - Metadata config
     * @returns {{schema: {}, operands: Array}} Return lucene builder object
     * @static
     */
    static convertMetadataToLuceneCompatible(data) {
        /*
         {
            name: 'Test',
            photo_url: 'url'
         }
         */
        const schema = {};
        let operands = [];

        const types = {
            'name': (value) => {
                const key = 'name';
                const tempObject = {};

                tempObject[key] = value;

                operands.push(tempObject);
            },
            'photo_url': (value) => {
                const key = 'metadata.photo_url';
                if (value) {
                    const tempObject = {};
                    // TODO: refactor this! with that way it will punch elastic performance right into balls!
                    tempObject[key] = {
                        '$from': '*',
                        '$to': '*'
                    };

                    operands.push(tempObject);
                }
            },
            'default': (config, unspecifiedKey) => {
                const tempObject = {};
                tempObject[`metadata.${unspecifiedKey}`] = 'Unspecified key';

                operands.push(tempObject);
            }
        };

        Object.keys(data).forEach(key => (types[key] || types.default)(data[key], key));

        if (operands.length > 1) {
            operands = {
                $operator: 'and',
                $operands: operands
            };
        } else if (operands.length === 1) {
            operands = operands[0];
        }

        return { schema, operands };
    }

    /**
     * Convert alerts object to lucene builder compatible object
     * @param {{status: Array<number>}} data - Alerts config
     * @returns {{schema: {}, operands: Array}} Return lucene builder object
     * @static
     */
    static convertAlertsToLuceneCompatible(data) {
        /*
        {
            status: [0, 1]
         }
         */
        const schema = {};
        let operands = [];

        const types = {
            'status': (states) => {
                const key = 'alerts.status';

                states.forEach((state) => {
                    const tempObject = {};

                    tempObject[key] = state.toString();

                    operands.push(tempObject);
                });
            },
            'default': (config, unspecifiedKey) => {
                const tempObject = {};
                tempObject[`alerts.${unspecifiedKey}`] = 'Unspecified key';

                operands.push(tempObject);
            }
        };

        Object.keys(data).forEach(key => (types[key] || types.default)(data[key], key));

        if (operands.length > 1) {
            operands = {
                $operator: 'or',
                $operands: operands
            };
        } else if (operands.length === 1) {
            operands = operands[0];
        }

        return { schema, operands };
    }

    /**
     * Convert users object to lucene builder compatible object
     * @param {Array<number>} data - Users config
     * @returns {{schema: {}, operands: Array}} Return lucene builder object
     * @static
     */
    static convertUsersToLuceneCompatible(data) {
        /*
         [0, 1, 2],
         */
        const schema = {};
        let operands = [];
        const key = 'users';

        data.forEach((userId) => {
            const tempObject = {};

            tempObject[key] = userId.toString();

            operands.push(tempObject);
        });

        if (operands.length > 1) {
            operands = {
                $operator: 'or',
                $operands: operands
            };
        } else if (operands.length === 1) {
            operands = operands[0];
        }

        return { schema, operands };
    }

    static convertFoldersToLuceneCompatible(data) {
        /*
         [0, 1, 2],
         */
        const key = 'folder';

        const operands = data.map(folderId => folderId.toString());

        if (operands.length > 1) {
            return `${key}:(${operands.join(' ')})`;
        } else if (operands.length === 1) {
            return `${key}:${operands[0]}`;
        }

        return '';
    }

    static convertLabelsToLuceneCompatible(data) {
        const operands = [];
        const key = 'labels';
        let query = '';

        data.forEach((label) => {
            operands.push(label.id.toString());
        });

        if (operands.length > 1) {
            query = `${key}:(${operands.join(' ')})`;
        } else if (operands.length === 1) {
            query = `${key}:${operands[0]}`;
        }

        return query;
    }

    static convertGeoDistanceToLuceneCompatible(data) {
        // geo_distance:{distance:500km, location:{lat:10,lon:102}}
        return `geo_distance:${JSON.stringify(data).replace(/"/g, '')}`;
    }

    /**
     * Convert things object to lucene builder compatible object
     * @param {Array<Object>} data - Things config
     * @returns {{schema: {}, operands: Array}} Return lucene builder object
     * @static
     */
    static convertThingsToLuceneCompatible(data) {
        /*
        [
            {
                type: 'temperature',
                state: {
                    above: 5,
                    below: 10
                },
            },
            {
                type: 'door',
                state: {
                    open: false,
                    closed: true
                },
            },
            {
                type: 'window',
                state: {
                    open: false,
                    closed: true
                },
            },
            {
                type: 'theft',
                state: {
                    present: true,
                    absent: false
                },
            },
            {
                type: 'cloud-connector',
                state: {
                    withSensors: true,
                    withoutSensors: false
                },
            }
        ]
        */
        const schema = {};
        let operands = [];

        const constructOperands = (key, value, localOperands) => {
            let tempOperands = localOperands.concat([value]);
            tempOperands = {
                $operator: 'and',
                $operands: tempOperands
            };

            operands.push(tempOperands);
        };

        const types = {
            'temperature': (config) => {
                const localOperands = [{ 'type': 'temperature' }];

                if ('state' in config && config.state) {
                    const stateKey = 'state.properties.temperature';
                    const stateValue = Lucene.convertTemperatureToLucene(config.state, stateKey);

                    if (stateValue) {
                        constructOperands(stateKey, stateValue, localOperands);
                    } else {
                        operands.push(localOperands[0]);
                    }
                } else {
                    operands.push(localOperands[0]);
                }
            },
            'door': (config) => {
                const localOperands = [{ 'type': 'door' }];

                if ('state' in config && config.state) {
                    const stateKey = 'state.properties.object_present';
                    const stateValue = Lucene.convertDoorToLucene(config.state, stateKey);

                    if (stateValue) {
                        constructOperands(stateKey, stateValue, localOperands);
                    } else {
                        operands.push(localOperands[0]);
                    }
                } else {
                    operands.push(localOperands[0]);
                }
            },
            'window': (config) => {
                const localOperands = [{ 'type': 'window' }];

                if ('state' in config && config.state) {
                    const stateKey = 'state.properties.object_present';
                    const stateValue = Lucene.convertWindowToLucene(config.state, stateKey);

                    if (stateValue) {
                        constructOperands(stateKey, stateValue, localOperands);
                    } else {
                        operands.push(localOperands[0]);
                    }
                } else {
                    operands.push(localOperands[0]);
                }
            },
            'proximity': (config) => {
                const localOperands = [{ 'type': 'proximity' }];

                if ('state' in config && config.state) {
                    const stateKey = 'state.properties.object_present';
                    const stateValue = Lucene.convertProximityToLucene(config.state, stateKey);

                    if (stateValue) {
                        constructOperands(stateKey, stateValue, localOperands);
                    } else {
                        operands.push(localOperands[0]);
                    }
                } else {
                    operands.push(localOperands[0]);
                }
            },
            'touch': () => {
                operands.push({
                    'type': 'touch'
                });
            },
            'theft': (config) => {
                const localOperands = [{ 'type': 'theft' }];

                if ('state' in config && config.state) {
                    const stateKey = 'state.properties.object_present';
                    const stateValue = Lucene.convertTheftToLucene(config.state, stateKey);

                    if (stateValue) {
                        constructOperands(stateKey, stateValue, localOperands);
                    } else {
                        operands.push(localOperands[0]);
                    }
                } else {
                    operands.push(localOperands[0]);
                }
            },
            'cloud-connector': () => {
                operands.push({
                    'type': 'cloud-connector'
                });
                // TODO: need specification for that case
                // if ('state' in config && config.state) {
                //     const stateKey = 'state.object_present';
                //     const stateValue = Lucene.convertCloudConnectorToLucene(config.state, stateKey);
                //
                //     if (stateValue) {
                //         constructOperands(stateKey, stateValue, localOperands);
                //     } else {
                //         operands.push(localOperands);
                //     }
                // }
            },
            'default': (config, unspecifiedType) => {
                const tempObject = {};
                tempObject['config.type'] = `Unspecified thing type: ${unspecifiedType}`;

                operands.push(tempObject);
            }
        };

        data.forEach(thing => (types[thing.type] || types.default)(thing, thing.type));

        if (operands.length > 1) {
            operands = {
                $operator: 'or',
                $operands: operands
            };
        } else if (operands.length === 1) {
            operands = operands[0];
        }

        return { schema, operands };
    }

    /**
     * Convert battery value to lucene builder compatible object
     * @param {{above: number, below: number}} config - Battery config
     * @returns {null|{$from: number, $to: number}} Return lucene builder object
     * @static
     */
    static convertBatteryToLucene(config) {
        let value = null;

        if ('above' in config && 'below' in config) {
            value = {
                $from: (config.above).toString(),
                $to: (config.below).toString(),
            };
        } else if ('above' in config && !('below' in config)) {
            value = {
                $from: (config.above).toString(),
                $to: '*',
            };
        } else if (!('above' in config) && 'below' in config) {
            value = {
                $from: '*',
                $to: (config.below).toString(),
            };
        }

        return value;
    }

    /**
     * Convert rssi value to lucene builder compatible object
     * @param {{above: number, below: number}} config - Rssi config
     * @returns {null|{$from: number, $to: number}} Return lucene builder object
     * @static
     */
    static convertRssiToLucene(config) {
        let value = null;

        if ('above' in config && 'below' in config) {
            value = {
                $from: (config.above).toString(),
                $to: (config.below).toString(),
            };
        } else if ('above' in config && !('below' in config)) {
            value = {
                $from: (config.above).toString(),
                $to: '*',
            };
        } else if (!('above' in config) && 'below' in config) {
            value = {
                $from: '*',
                $to: (config.below).toString(),
            };
        }

        return value;
    }

    /**
     * Convert temperature value to lucene builder compatible object
     * @param {{above: number, below: number}} config - Temperature config
     * @param {string} key - Name of lucene field
     * @returns {null|{$from: number, $to: number}|{$operands: Array<Object>}} Return lucene builder object
     * @static
     */
    static convertTemperatureToLucene(config, key) {
        let value = null;

        if ('above' in config && 'below' in config) {
            value = {
                $from: (config.above).toString(),
                $to: (config.below).toString(),
            };
        } else if ('above' in config && !('below' in config)) {
            value = {
                $from: (config.above).toString(),
                $to: '*',
            };
        } else if (!('above' in config) && 'below' in config) {
            value = {
                $from: '*',
                $to: (config.below).toString(),
            };
        }

        if (value) {
            const tempObj = {};
            tempObj[key] = value;

            return {
                $operands: [tempObj]
            };
        }

        return value;
    }

    /**
     * Convert door value to lucene builder compatible object
     * @param {{open: boolean, closed: boolean}} config - Door config
     * @param {string} key - Name of lucene field
     * @returns {null|{$operator: string, $operands: Array<Object>}} Return lucene builder object
     * @static
     */
    static convertDoorToLucene(config, key) {
        return Lucene.convertProximityToLucene(config, key);
    }

    /**
     * Convert window value to lucene builder compatible object
     * @param {{open: boolean, closed: boolean}} config - Window config
     * @param {string} key - Name of lucene field
     * @returns {null|{$operator: string, $operands: Array<Object>}} Return lucene builder object
     * @static
     */
    static convertWindowToLucene(config, key) {
        return Lucene.convertProximityToLucene(config, key);
    }

    /**
     * Convert proximity value to lucene builder compatible object
     * @param {{open: boolean, closed: boolean}} config - Window config
     * @param {string} key - Name of lucene field
     * @returns {null|{$operator: string, $operands: Array<Object>}} Return lucene builder object
     * @static
     */
    static convertProximityToLucene(config, key) {
        let value = null;
        const open = {};
        const closed = {};

        if ('open' in config && 'closed' in config) {
            open[key] = 'false';
            closed[key] = 'true';
            value = {
                $operator: 'OR',
                $operands: [open, closed]
            };
        } else if ('open' in config && !('closed' in config)) {
            value = {
                [key]: 'false'
            };
        } else if (!('open' in config) && 'closed' in config) {
            value = {
                [key]: 'true'
            };
        }

        return value;
    }

    /**
     * Convert theft value to lucene builder compatible object
     * @param {{present: boolean, absent: boolean}} config - Theft config
     * @param {string} key - Name of lucene field
     * @returns {null|{$operator: string, $operands: Array<Object>}} Return lucene builder object
     * @static
     */
    static convertTheftToLucene(config, key) {
        let value = null;
        const present = {};
        const absent = {};

        if ('present' in config && 'absent' in config) {
            present[key] = 'true';
            absent[key] = 'false';
            value = {
                $operator: 'OR',
                $operands: [present, absent]
            };
        } else if ('present' in config && !('absent' in config)) {
            value = {
                [key]: 'true'
            };
        } else if (!('present' in config) && 'absent' in config) {
            value = {
                [key]: 'false'
            };
        }

        return value;
    }

    static convertDateRangeToLuceneCompatible(config) {
        let value = null;

        if (config.after && config.before) {
            value = {
                $from: config.after,
                $to: config.before,
            };
        } else if (config.after && !config.before) {
            value = {
                $from: config.after,
                $to: '*',
            };
        }

        return value;
    }
}



// WEBPACK FOOTER //
// ./app/services/lucene/index.js